import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Stack,
  Typography,
  styled,
} from "@mui/material";
import {
  selectActiveSession,
  selectSelectedSchool,
} from "app/modules/schools/selectors";
import { getTcById } from "app/services/student-management";
import React, { useRef, useState } from "react";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { useReactToPrint } from "react-to-print";
import { toast } from "react-toastify";

import TcTemplate1 from "./TcTemplate1";
import TcTemplate2 from "./TcTemplate2";
import { CustomSelectBox } from "assets/styles/globalStyledComponent";

const DEFAULT_FIELD_VISIBILITY = {
  showAddress: false,
  showRegNumber: true,
  showBookNumber: true,
  showAdmissionNumber: true,
  showSrNumber: true,
  showPenNumber: true,
  showSchoolNo: true,
  showAffiliationNumber: true,
  showEmail: true,
  showContactNo: true,
  showWebsite: true,
};

const FIELD_VISIBILITY_OPTIONS = [
  { key: "showAddress", label: "Show Address" },
  { key: "showRegNumber", label: "Reg Number" },
  { key: "showBookNumber", label: "Book Number" },
  { key: "showAdmissionNumber", label: "Admission Number" },
  { key: "showSrNumber", label: "SR Number" },
  { key: "showPenNumber", label: "PEN Number" },
  { key: "showSchoolNo", label: "School No" },
  { key: "showAffiliationNumber", label: "Affiliation Number" },
  { key: "showEmail", label: "Email" },
  { key: "showContactNo", label: "Contact No" },
  { key: "showWebsite", label: "Website" },
];

const Preview = ({ id }) => {
  const selectedSession = useSelector(selectActiveSession);
  const selectedSchool = useSelector(selectSelectedSchool);
  const [data, setData] = useState({});
  const printRef = useRef(null);
  const [color, setcolor] = useState("#e6ffe9");
  const [template, setTemplate] = useState(1);
  const [fieldVisibility, setFieldVisibility] = useState(
    DEFAULT_FIELD_VISIBILITY,
  );
  const [showWatermark, setShowWatermark] = useState(false);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `TransferCertificate-${data?.firstName}
                    ${data?.middleName} ${data?.lastName}`,
  });
  const { isLoading: studentDataForMigrateLoading, isFetching } = useQuery({
    queryKey: ["getTcById"],
    queryFn: () =>
      getTcById({
        session: selectedSession,
        transferCertificateDocId: id,
      }),
    onSuccess: ({ data }) => {
      toast.success(data?.message);
      setData(data?.obj);
    },

    onError: (err) => {
      toast.error(err?.response?.data?.message);
    },
  });

  const handleChangeTemplate = (value) => {
    setTemplate(value);
  };

  const handleVisibilityChange = (key) => (e) => {
    setFieldVisibility((prev) => ({
      ...prev,
      [key]: e.target.checked,
    }));
  };

  return (
    <>
      {studentDataForMigrateLoading || isFetching ? (
        <Stack direction="row" justifyContent="center" sx={{ py: 5 }}>
          <CircularProgress />
        </Stack>
      ) : (
        <>
          <Stack
            sx={{ m: 1 }}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
          >
            <Button variant="contained" color="secondary" onClick={handlePrint}>
              Print
            </Button>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showWatermark}
                  onChange={(e) => setShowWatermark(e.target.checked)}
                  color="secondary"
                  disabled={!selectedSchool?.schoolLogo}
                />
              }
              label="Show Watermark"
            />
            <Stack direction={"row"} gap={2} alignItems={"center"}>
              <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
                Select Template :
              </Typography>
              <CustomSelectBox
                label="Select Template"
                value={template}
                onChange={(e) => handleChangeTemplate(e.target.value)}
              >
                <MenuItem value={1}>Template 1</MenuItem>
                <MenuItem value={2}>Template 2</MenuItem>
              </CustomSelectBox>
            </Stack>

            <Stack direction="row" gap={1.5}>
              <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
                Change Color :
              </Typography>
              <ColorBox
                sx={{
                  backgroundColor: "transparent",
                  transform: color === "#fff" && "scale(1.5)",
                }}
                onClick={() => setcolor("#fff")}
              />
              <ColorBox
                sx={{
                  backgroundColor: "#e6ffe9",
                  transform: color === "#e6ffe9" && "scale(1.5)",
                }}
                onClick={() => setcolor("#e6ffe9")}
              />
              <ColorBox
                sx={{
                  backgroundColor: "#fff5b9",
                  transform: color === "#fff5b9" && "scale(1.5)",
                }}
                onClick={() => setcolor("#fff5b9")}
              />
            </Stack>
          </Stack>
          <Stack
            direction="row"
            flexWrap="wrap"
            gap={0.5}
            alignItems="center"
            sx={{ mx: 1, mb: 1 }}
          >
            {FIELD_VISIBILITY_OPTIONS.map(({ key, label }) => (
              <FormControlLabel
                key={key}
                control={
                  <Checkbox
                    size="small"
                    checked={Boolean(fieldVisibility[key])}
                    onChange={handleVisibilityChange(key)}
                    color="secondary"
                  />
                }
                label={label}
                sx={{
                  mr: 1,
                  "& .MuiFormControlLabel-label": {
                    fontSize: "13px",
                    fontWeight: 500,
                  },
                }}
              />
            ))}
          </Stack>
          {template === 1 && (
            <TcTemplate1
              data={data}
              color={color}
              printRef={printRef}
              showWatermark={showWatermark}
              {...fieldVisibility}
            />
          )}
          {template === 2 && (
            <TcTemplate2
              data={data}
              color={color}
              printRef={printRef}
              showWatermark={showWatermark}
              {...fieldVisibility}
            />
          )}
        </>
      )}
    </>
  );
};

export default Preview;
const ColorBox = styled(Box)`
  height: 25px;
  width: 25px;
  border-radius: 50%;
  border: 1px solid gray;
  cursor: pointer;
  box-shadow:
    rgba(50, 50, 93, 0.25) 0px 2px 5px -1px,
    rgba(0, 0, 0, 0.3) 0px 1px 3px -1px;
`;
